import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { getItems } from 'src/services/client';

export interface CatalogItem {
  id: string;
  name: string;
  properties?: { name: string }[];
  propertyNames?: string;
  typeName?: string;
  typeId?: string;
  [key: string]: any;
}

interface UseItemsReturn {
  items: CatalogItem[];
  isLoading: boolean;
  error: string | null;
}

export const withPropertyNames = (items: CatalogItem[]): CatalogItem[] => {
  return items.map((item: CatalogItem) => {
    const propertyNames: string = (item.properties ?? [])
      .map((property: { name: string }) => property.name)
      .join(', ');

    return { ...item, propertyNames };
  });
};

export const fetchItemsData = async (
  userId: string,
  role: string
): Promise<CatalogItem[]> => {
  const response: any = await getItems(userId, role);

  if (!response || (!response.ok && response.status !== 200)) {
    throw new Error('Failed to fetch items');
  }

  const responseJson: CatalogItem[] = await response.json();

  return withPropertyNames(responseJson ?? []);
};

export const useItems = (): UseItemsReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const [items, setItems] = useState<CatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const userId: string = user?.id;
  const role: string = user?.roles?.[0]?.role?.name;

  useEffect(() => {
    if (!userId || !role) {
      return;
    }

    const loadItems = async (): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const result: CatalogItem[] = await fetchItemsData(userId, role);
        setItems(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    loadItems();
  }, [userId, role]);

  return { items, isLoading, error };
};

export default useItems;
